import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import {
  Card,
  TextField,
  Button,
  MenuItem,
  IconButton,
  DialogProps,
  Tabs,
  Tab,
  InputAdornment,
  Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';
import Preloader from '../../../../components/other/Preloader/Preloader';
import { Modal } from '../../../../components/other/Modals';
import CompaniesTable from '../../../../components/tableComponents/Table/CompaniesTable';
import { IAppState } from '../../../../store/rootDuck';
import { actions as categoriesActions } from '../../../../store/ducks/categories.duck';
import { actions as usersActions } from '../../../../store/ducks/users.duck';
import { actions as companiesActions } from '../../../../store/ducks/companies.duck';
import { actions as profileActions } from '../../../../store/ducks/profile.duck';
import { setLayoutFooter, setLayoutSubheader } from '../../../../utils/layout';
import { usersBreadCrumbs } from './utils/createBreadCrumbs';
import { IUser, UserRoles } from '../../../../interfaces/user';
import homeStyles from '../../../../constants/homeStyles';
import { ICompany } from '../../../../interfaces/companies';
import { useStylesEditPage } from './hooks/useStyleEditPage';
import {
  useDefineUserRole,
  useFormatMessage,
  useShowErrors,
  useShowSuccesses,
} from '../../../../hooks';
import { useFormikUserEdit } from './hooks/useFormikUserEdit';
import { useAxiosGetCallback } from '../../../../hooks/useAxiosGet';
import { useHandleCountrySelect } from '../../../auth/hooks/useHandleCountrySelect';
import { UserListType } from '../constants';
import { getRole } from './utils';
import { IMaskPhoneInput } from '../../../auth/components/CheckInSystem';
import CountrySelect from '../../../../components/ui/CountrySelect';

const UsersEditPage: React.FC<TPropsFromRedux> = ({
  me,
  user,
  fetchMe,
  fetch,
  clearFetchUser,
  loading,
  clearEdit,
  add,
  edit,
  delUser,
  clearDelUser,
  delUserSuccess,
  delUserError,
  delUserLoading,
  editSuccess,
  editLoading,
  editError,
  editCompany,
  editCompanySuccess,
  editCompanyError,
  editCompanyLoading,
  searchCompany,
  fetchCompanies,
  loadingSearchCompanies,
  companies,
  pageCompanies,
  perPageCompanies,
  totalCompanies,
  loadingCompanies,
  clearEditCompany,
  clearFetchCompanies,
  clearSearchCompany,
  byIdError,
  companiesErr,
  companiesSearchErr,
  clearPagination,
  addSuccess,
  addUserId,
  clearAddUserId,
  company,
  fetchCompany,
}) => {
  const { userId, type } = useParams();
  const { state } = useLocation();
  const id = userId;
  const [open, setOpen] = useState(false);
  const [openDialogCompanies, setOpenDialogCompanies] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(show => !show);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const handleClickShowPasswordRepeat = () => setShowPasswordRepeat(show => !show);

  const [tabValue, setTabValue] = useState(0);
  const fm = useFormatMessage();
  const role = getRole(type as UserListType, true);
  // const selectStyle = useSelectStyle();
  const isRoleManager = useDefineUserRole(me, 'ROLE_MANAGER');
  const isVendorStaff = useDefineUserRole(user, 'ROLE_VENDOR_STAFF');
  const isBuyerStaff = useDefineUserRole(user, 'ROLE_BUYER_STAFF');
  const isVendor = useDefineUserRole(user, 'ROLE_VENDOR');
  const isBuyer = useDefineUserRole(user, 'ROLE_BUYER');
  const classes = useStylesEditPage();
  const homeClasses = homeStyles();
  const navigate = useNavigate();
  const intl = useIntl();
  const {
    data: countries,
    loading: countriesLoading,
    makeRequest,
  } = useAxiosGetCallback<any>();
  const { countryId, setCountryId, countryData } = useHandleCountrySelect(countries, 1);

  useLayoutEffect(() => {
    if (me?.is_admin && user?.company?.id) {
      fetchCompany(user?.company?.id);
    }
  }, [user, me]);

  useEffect(() => {
    makeRequest('/api/phone_codes');
  }, []);
  setLayoutSubheader({
    title: id ? fm('USER.EDIT.TITLE') : fm('USER.ADD.TITLE'),
    breadcrumb: me?.is_admin ? usersBreadCrumbs(intl, type || '', clearPagination) : [],
  });
  setLayoutFooter({ show: true });

  useEffect(() => {
    fetchMe();
  }, []);

  useEffect(() => {
    if (id) fetch(Number(id));
  }, [id]);

  useEffect(() => {
    if (user?.country?.id) {
      setCountryId(user.country.id);
    }
  }, [user]);

  // formik
  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } =
    useFormikUserEdit(id, user, edit, add, countryData, role, me);

  const deleteCompany = useCallback(() => {
    if (!company) return;
    if (!user?.company) return;
    if (id) {
      const ids = company.managers.map(item => item.id).filter(id => id !== user.id);
      editCompany({ id: Number(user?.company?.id), data: { managers_ids: ids } });
    } else {
      setFieldValue('company', undefined);
    }
  }, [userId, user?.company, company, id]);

  const connectCompany = useCallback(
    (userId2: number, company: ICompany) => {
      if (id) {
        const filterManagers =
          role === 'ROLE_VENDOR'
            ? company.managers.filter(i => i.roles[0] !== 'ROLE_VENDOR')
            : company.managers;
        editCompany({
          id: company.id,
          data: { managers_ids: [...filterManagers.map(i => i.id), userId2] },
        });
      } else {
        setFieldValue('company', company);
        setOpenDialogCompanies(false);
      }
    },
    [id]
  );

  const openSearchCompaniesDialog = useCallback(() => {
    if (values.companyInn === '') return null;
    searchCompany({ page: 1, perPage: 20, inn: values.companyInn });
    setOpenDialogCompanies(true);
  }, [values]);

  const openCompaniesDialog = useCallback(() => {
    fetchCompanies({ perPage: 20, page: pageCompanies });
    setOpenDialogCompanies(true);
  }, [values]);

  useEffect(() => {
    state?.company && setFieldValue('company', state.company);
  }, [state]);

  // after success
  useEffect(() => {
    if (addSuccess && addUserId) {
      if (tabValue === 1 && values.role) {
        const userType =
          // eslint-disable-next-line no-nested-ternary
          values.role === 'ROLE_VENDOR'
            ? UserListType.VENDOR
            : // eslint-disable-next-line no-nested-ternary
            values.role === 'ROLE_BUYER'
            ? UserListType.BUYER
            : values.role === 'ROLE_MANAGER'
            ? UserListType.MANAGER
            : null;
        userType && navigate(`/users/edit/${userType}/${addUserId}`, { replace: true });
      } else {
        navigate(-1);
      }
    } else if ((delUserSuccess || editSuccess) && tabValue === 0) {
      navigate(-1);
    }
  }, [delUserSuccess, editSuccess, navigate, addSuccess, addUserId]);

  useEffect(() => {
    if (addUserId && id) {
      setTabValue(3);
      clearAddUserId();
    }
  }, [addUserId, id]);

  useEffect(() => {
    editError && tabValue === 1 && setTabValue(0);
  }, [editError]);

  const handleChangeTabPhoto = useCallback(
    (newValue: number) => {
      handleSubmit();
      if (
        values.login &&
        values.email &&
        values.password &&
        values.password2 &&
        values.password === values.password2 &&
        values.role
      ) {
        setTabValue(newValue);
      }
    },
    [handleSubmit, values, setTabValue]
  );

  useEffect(() => {
    if (editCompanySuccess) {
      setOpenDialogCompanies(false);
      id && fetch(Number(id));
    }
  }, [editCompanySuccess, editCompanyError, id]);

  // notifications
  useShowErrors([
    byIdError,
    delUserError,
    editError,
    companiesErr,
    companiesSearchErr,
    editCompanyError,
  ]);
  useShowSuccesses([{ when: editSuccess, msg: fm('TITLE.SAVED') }]);

  useEffect(() => {
    if (byIdError) {
      navigate(-1);
      clearFetchUser();
    }
  }, [byIdError]);

  // clean up
  useEffect(() => {
    return () => {
      clearFetchUser();
      clearDelUser();
      clearEdit();
      clearFetchCompanies();
      clearSearchCompany();
      clearEditCompany();
    };
  }, []);

  const title: any = {
    ROLE_BUYER: intl.formatMessage({ id: 'USER.EDIT.BUYER.TITLE' }),
    ROLE_BUYER_STAFF: intl.formatMessage({ id: 'USER.EDIT.BUYER_STAFF.TITLE' }),
    ROLE_VENDOR: intl.formatMessage({ id: 'USER.EDIT.VENDOR.TITLE' }),
    ROLE_VENDOR_STAFF: intl.formatMessage({ id: 'USER.EDIT.VENDOR_STAFF.TITLE' }),
  };

  if (loading) return <Preloader />;

  return (
    <>
      <Row>
        <Col>
          <Card className={homeClasses.classes.container}>
            <div className={homeClasses.classes.headerGradient} />
            <Tabs
              value={tabValue}
              onChange={(_: React.ChangeEvent<{}>, newValue: number) => {
                if (newValue === 1 && !id) {
                  handleChangeTabPhoto(newValue);
                } else {
                  setTabValue(newValue);
                }
              }}
              indicatorColor='primary'
              textColor='primary'
            >
              <Tab label={fm('PRODUCT.TABS.COMMON')} />
            </Tabs>
            <div style={{ width: '100%', padding: 24 }}>
              <Typography variant='h2' className={homeClasses.classes.title}>
                {title[role]}
              </Typography>
            </div>
            <div style={{ display: tabValue === 0 ? 'contents' : 'none' }}>
              <form
                className={homeClasses.classes.editUserForm}
                onSubmit={handleSubmit}
                autoComplete='off'
              >
                <div className={homeClasses.classes.leftAndRightContainers}>
                  <TextField
                    type='text'
                    label={fm('USER.EDIT.LOGIN')}
                    margin='normal'
                    name='login'
                    value={values.login}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.login && errors.login}
                    error={Boolean(touched.login && errors.login)}
                  />

                  <TextField
                    type='text'
                    label={fm('USER.EDIT.EMAIL')}
                    margin='normal'
                    name='email'
                    value={values.email}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.email && errors.email}
                    error={Boolean(touched.email && errors.email)}
                  />

                  <div className={classes.dates}>
                    <div className={classes.country}>
                      <CountrySelect
                        label={intl.formatMessage({ id: 'USER.INPUT.COUNTRY_CODE' })}
                        countries={countries}
                        countryId={countryId}
                        setCountryId={setCountryId}
                        countriesLoading={countriesLoading}
                        showCode
                        variant='outlined'
                      />
                    </div>
                    {countryData && (
                      <IMaskPhoneInput
                        mask={countryData.mask}
                        onBlur={handleBlur}
                        value={values.phoneNumbers}
                        onChange={(e: any) => {
                          setFieldValue(
                            'phoneNumbers',
                            e.target.value ? e.target.value.replace(/[^0-9]/g, '') : ''
                          );
                        }}
                        type='tel'
                        label={intl.formatMessage({ id: 'AUTH.INPUT.PHONE' })}
                        margin='normal'
                        className='kt-width-full'
                        name='phoneNumbers'
                        helperText={touched.phoneNumbers && errors.phoneNumbers}
                        error={Boolean(touched.phoneNumbers && errors.phoneNumbers)}
                        variant='outlined'
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              {' '}
                              +{countryData.code}{' '}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </div>

                  <TextField
                    autoComplete='off'
                    type={showPassword ? 'text' : 'password'}
                    label={fm('USER.EDIT.PASSWORD')}
                    margin='normal'
                    name='password'
                    value={values.password}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.password && errors.password}
                    error={Boolean(touched.password && errors.password)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={handleClickShowPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    autoComplete='off'
                    type={showPasswordRepeat ? 'text' : 'password'}
                    margin='normal'
                    label={fm('USER.EDIT.PASSWORD.REPEAT')}
                    name='password2'
                    value={values.password2}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.password2 && errors.password2}
                    error={Boolean(touched.password2 && errors.password2)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={handleClickShowPasswordRepeat}
                          >
                            {showPasswordRepeat ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <div className={`${classes.actions} ${classes.hideIfMobile}`}>
                    <Button
                      onClick={() => navigate(-1)}
                      className={classes.buttons}
                      variant='contained'
                      color='success'
                      fullWidth
                    >
                      {fm('COMMON.BUTTON.BACK')}
                    </Button>
                  </div>
                </div>
                <div className={homeClasses.classes.leftAndRightContainers}>
                  <TextField
                    select
                    margin='normal'
                    label={fm('USER.EDIT.ROLE')}
                    value={values.role}
                    onChange={handleChange}
                    name='role'
                    variant='outlined'
                    helperText={touched.role && errors.role}
                    error={Boolean(touched.role && errors.role)}
                    disabled={
                      (me?.is_admin || isRoleManager) &&
                      (isVendorStaff || isBuyerStaff || isVendor || isBuyer) &&
                      !!id
                        ? false
                        : !!id || !!role
                    }
                  >
                    <MenuItem
                      hidden={!isVendorStaff && !isVendor}
                      value={UserRoles.ROLE_VENDOR}
                    >
                      {fm('USER.ROLES.VENDOR')}
                    </MenuItem>
                    <MenuItem hidden value={UserRoles.ROLE_ADMIN}>
                      {fm('USER.ROLES.ADMIN')}
                    </MenuItem>
                    <MenuItem hidden value={UserRoles.ROLE_MANAGER}>
                      {fm('USER.ROLES.MANAGER')}
                    </MenuItem>
                    <MenuItem hidden={!isBuyerStaff && !isBuyer} value={UserRoles.ROLE_BUYER}>
                      {fm('USER.ROLES.BUYER')}
                    </MenuItem>
                    <MenuItem
                      hidden={!isVendorStaff && !isVendor}
                      value={UserRoles.ROLE_VENDOR_STAFF}
                    >
                      {fm('USER.ROLES.VENDOR.STAFF')}
                    </MenuItem>
                    <MenuItem
                      hidden={!isBuyerStaff && !isBuyer}
                      value={UserRoles.ROLE_BUYER_STAFF}
                    >
                      {fm('USER.ROLES.BUYER.STAFF')}
                    </MenuItem>
                  </TextField>

                  <TextField
                    type='text'
                    label={intl.formatMessage({ id: 'USER.EDIT.LAST_NAME' })}
                    margin='normal'
                    name='last_name'
                    value={values.last_name || ''}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.last_name && errors.last_name}
                    error={Boolean(touched.last_name && errors.last_name)}
                  />

                  <TextField
                    type='text'
                    label={intl.formatMessage({ id: 'USER.EDIT.FIRST_NAME' })}
                    margin='normal'
                    name='first_name'
                    value={values.first_name || ''}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.first_name && errors.first_name}
                    error={Boolean(touched.first_name && errors.first_name)}
                  />

                  <TextField
                    type='text'
                    label={intl.formatMessage({ id: 'USER.EDIT.MIDDLE_NAME' })}
                    margin='normal'
                    name='middle_name'
                    value={values.middle_name || ''}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText={touched.middle_name && errors.middle_name}
                    error={Boolean(touched.middle_name && errors.middle_name)}
                  />

                  <div className={classes.companyField}>
                    <TextField
                      label={fm('COMPANY.FORM.COMPANY')}
                      margin='normal'
                      name={values.company?.name ? 'company' : 'companyInn'}
                      placeholder={fm('COMPANY.SEARCH.BY_INN')}
                      value={values.company?.name || values.companyInn}
                      onChange={handleChange}
                      variant='outlined'
                      disabled={!!values.company?.name}
                      InputProps={{
                        endAdornment: values.company?.name ? (
                          <IconButton onClick={deleteCompany}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        ) : (
                          <IconButton onClick={openSearchCompaniesDialog}>
                            <SearchIcon color='primary' fontSize='small' />
                          </IconButton>
                        ),
                      }}
                    />
                    {!values.company?.name && me?.is_admin && (
                      <Button
                        style={{
                          marginTop: 8,
                          marginLeft: 10,
                          height: 52,
                        }}
                        variant='contained'
                        color='primary'
                        onClick={openCompaniesDialog}
                      >
                        {fm('COMPANY.BUTTON.ALL')}
                      </Button>
                    )}
                  </div>
                  <div className={`${classes.actions} ${classes.userAcitons}`}>
                    <Button
                      onClick={() => navigate(-1)}
                      className={`${classes.buttons} ${classes.showIfMobile}`}
                      variant='contained'
                      color='success'
                    >
                      {fm('COMMON.BUTTON.BACK')}
                    </Button>

                    <ButtonWithLoader
                      disabled={editLoading}
                      loading={editLoading || editCompanyLoading}
                      fullWidth
                    >
                      {id ? fm('USER.BUTTON.EDIT') : fm('USER.BUTTON.ADD')}
                    </ButtonWithLoader>

                    {id && (
                      <Button
                        onClick={() => {
                          setOpen(true);
                        }}
                        className={classes.btnDel}
                        variant='contained'
                        color='error'
                        fullWidth
                      >
                        {fm('COMMON.BUTTON.DELETE')}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </Card>
        </Col>
      </Row>
      <Modal
        DialogProps={{ maxWidth: 'md' } as DialogProps}
        open={openDialogCompanies}
        onClose={editCompanyLoading ? () => {} : () => setOpenDialogCompanies(false)}
        title={fm('COMPANY.EDIT.MOUNT_TITLE')}
        loading={loadingCompanies || loadingSearchCompanies || editCompanyLoading}
        content={
          <CompaniesTable
            me={user as IUser}
            page={pageCompanies}
            perPage={perPageCompanies}
            total={totalCompanies}
            onConnectUser={company => {
              connectCompany(user?.id || 0, company);
            }}
            companies={companies}
            fetch={({ page, perPage }) => fetchCompanies({ page, perPage })}
          />
        }
        actions={[
          { title: fm('COMMON.BUTTON.CLOSE'), onClick: () => setOpenDialogCompanies(false) },
        ]}
      />

      <Modal
        open={open}
        onClose={delUserLoading ? () => {} : () => setOpen(false)}
        title={fm('USER.DELETE.TITLE')}
        loading={delUserLoading}
        content={delUserLoading ? undefined : fm('USER.DELETE.TEXT')}
        actions={
          delUserLoading
            ? undefined
            : [
                {
                  title: fm('COMMON.BUTTON.CANCEL'),
                  onClick: () => setOpen(false),
                },
                {
                  title: fm('COMMON.BUTTON.DELETE'),
                  onClick: () => id && delUser(+id),
                },
              ]
        }
      />
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    me: state.profile.me,
    user: state.users.user,
    loading: state.users.byIdLoading,
    editSuccess: state.users.editSuccess,
    editLoading: state.users.editLoading,
    editError: state.users.editError,
    byIdSuccess: state.users.byIdSuccess,
    byIdError: state.users.byIdError,
    delUserLoading: state.users.delUserLoading,
    delUserSuccess: state.users.delUserSuccess,
    delUserError: state.users.delUserError,

    editCompanySuccess: state.companies.editSuccess,
    editCompanyLoading: state.companies.editLoading,
    editCompanyError: state.companies.editError,
    pageCompanies: state.companies.page,
    perPageCompanies: state.companies.per_page,
    totalCompanies: state.companies.total,
    companies: state.companies.companies,
    companiesErr: state.companies.error,
    loadingSearchCompanies: state.companies.searchLoading,
    loadingCompanies: state.companies.loading,
    companiesSearchErr: state.companies.searchError,
    addSuccess: state.users.addSuccess,
    addUserId: state.users.addUserId,
    company: state.companies.company,
  }),
  {
    fetch: usersActions.fetchByIdRequest,
    clearFetchUser: usersActions.clearFetchById,
    clearEdit: usersActions.clearEdit,
    clearDelUser: usersActions.clearDelUser,
    add: usersActions.addRequest,
    edit: usersActions.editRequest,

    delUser: usersActions.delUserRequest,

    fetchMe: profileActions.fetchRequest,

    clearCompanies: companiesActions.clearCompanies,
    editCompany: companiesActions.editRequest,
    clearEditCompany: companiesActions.clearEdit,

    fetchCompanies: companiesActions.fetchRequest,
    clearFetchCompanies: companiesActions.clearFetch,

    searchCompany: companiesActions.searchRequest,
    clearSearchCompany: companiesActions.clearSearch,
    clearPagination: usersActions.clearPagination,
    clearAddUserId: usersActions.clearAddUserId,
    fetchCompany: companiesActions.fetchByIdRequest,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(UsersEditPage);
